import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type OrderPhase = Database['public']['Enums']['order_phase'];

interface OrderDialogProps {
  trigger: React.ReactNode;
  initial?: {
    client_name?: string;
    client_email?: string;
    client_phone?: string;
    team?: string;
    notes?: string;
    source_message_id?: string;
    source_lead_id?: string;
  };
  defaultOpen?: boolean;
  onCreated?: () => void;
}

const phaseLabels: Record<OrderPhase, string> = {
  inquiry: 'Poptávka',
  design: 'Design',
  airbrush: 'Airbrush',
  clear_coat: 'Clear Coat',
  done: 'Hotovo',
  cancelled: 'Zrušeno',
};

const CreateOrderDialog = ({ trigger, initial, onCreated }: OrderDialogProps) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_name: initial?.client_name ?? '',
    client_email: initial?.client_email ?? '',
    client_phone: initial?.client_phone ?? '',
    team: initial?.team ?? '',
    notes: initial?.notes ?? '',
    phase: 'inquiry' as OrderPhase,
    deadline: '',
    price: '',
  });

  useEffect(() => {
    if (open && initial) {
      setForm((f) => ({
        ...f,
        client_name: initial.client_name ?? f.client_name,
        client_email: initial.client_email ?? f.client_email,
        client_phone: initial.client_phone ?? f.client_phone,
        team: initial.team ?? f.team,
        notes: initial.notes ?? f.notes,
      }));
    }
  }, [open, initial]);

  const submit = async () => {
    if (!form.client_name.trim()) {
      toast.error('Jméno klienta je povinné');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('orders').insert({
      client_name: form.client_name.trim(),
      client_email: form.client_email.trim() || null,
      client_phone: form.client_phone.trim() || null,
      team: form.team.trim() || null,
      notes: form.notes.trim() || null,
      phase: form.phase,
      deadline: form.deadline || null,
      price: form.price ? Number(form.price) : null,
      source_message_id: initial?.source_message_id ?? null,
      source_lead_id: initial?.source_lead_id ?? null,
    });
    setSaving(false);
    if (error) {
      toast.error('Chyba: ' + error.message);
      return;
    }
    toast.success('Zakázka vytvořena');
    setOpen(false);
    onCreated?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nová zakázka</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Klient *</Label>
            <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Tým</Label>
            <Input value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Fáze</Label>
              <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v as OrderPhase })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(phaseLabels) as OrderPhase[]).map((p) => (
                    <SelectItem key={p} value={p}>{phaseLabels[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Termín</Label>
              <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
            <div>
              <Label>Cena (Kč)</Label>
              <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Poznámky</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Zrušit</Button>
          <Button onClick={submit} disabled={saving}>{saving ? 'Ukládám…' : 'Vytvořit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { phaseLabels };
export default CreateOrderDialog;
